'use client';

import type {
  EarningsChartBucket,
  Referee,
  ReferralSummary,
} from '@/components/portfolio/referrals/types';

/**
 * Referrals stub hooks.
 *
 * Referrals is not yet implemented on the v2 SDK / new backend stack, and the
 * UI is gated behind the `referrals` feature flag (and a `_fake-data.ts`
 * scaffold). These drop-in replacements for the old `@morfi/backend-sdk`
 * referral hooks let the referral components typecheck and render their empty
 * states with the legacy SDK removed.
 *
 * Each returns honest "feature off / no data" (empty list or `undefined` with
 * a settled `success` status) — never fabricated counts or earnings. When
 * referrals is rebuilt on v2, swap these imports back to generated SDK hooks.
 */

type StubStatus = 'idle' | 'loading' | 'success' | 'error';

const STUB_STATUS: StubStatus = 'success';

// Identity-stable empty results. Real TanStack Query hooks return a referentially
// stable `data` across renders (structural sharing); consumers depend on that —
// e.g. `<EarningsSparkline>`'s snapshot effect keys on `data` identity. Returning
// a fresh `[]` literal each render breaks that contract and spins those effects
// into an infinite setState→render loop ("Maximum update depth exceeded").
const EMPTY_REFEREES: readonly Referee[] = [];
const EMPTY_EARNINGS_BUCKETS: readonly EarningsChartBucket[] = [];

interface FieldsOption {
  readonly fields?: readonly string[];
}

interface ParamsOption {
  readonly params?: Readonly<Record<string, unknown>>;
}

interface HookOptions {
  readonly enabled?: boolean;
}

interface ReferrerData {
  readonly referrer: { readonly displayName: string | null };
}

// ALLOW-UNSCOPED: local stub — feature off, no backend call to field-scope.
export function useGetReferral(
  _userId: string,
  _options?: FieldsOption,
  _hookOptions?: HookOptions,
): { data: ReferralSummary | undefined; status: StubStatus } {
  return { data: undefined, status: STUB_STATUS };
}

// ALLOW-UNSCOPED: local stub — feature off, no backend call to field-scope.
export function useGetReferralReferrer(
  _userId: string,
  _options?: FieldsOption,
  _hookOptions?: HookOptions,
): { data: ReferrerData | undefined; status: StubStatus } {
  return { data: undefined, status: STUB_STATUS };
}

// ALLOW-UNSCOPED: local stub — feature off, no backend call to field-scope.
export function useGetReferralReferees(
  _userId: string,
  _options?: FieldsOption,
  _hookOptions?: HookOptions,
): { data: readonly Referee[]; status: StubStatus } {
  return { data: EMPTY_REFEREES, status: STUB_STATUS };
}

// ALLOW-UNSCOPED: local stub — feature off, no backend call to field-scope.
export function useGetReferralEarningsChart(
  _userId: string,
  _options?: ParamsOption,
  _hookOptions?: HookOptions,
): { data: readonly EarningsChartBucket[]; status: StubStatus } {
  return { data: EMPTY_EARNINGS_BUCKETS, status: STUB_STATUS };
}
